"use client"

import { type RefObject, useEffect, useRef } from "react"

const FOCUSABLE_SELECTOR = [
    "a[href]",
    "button:not([disabled])",
    "textarea:not([disabled])",
    "input:not([disabled])",
    "select:not([disabled])",
    '[tabindex]:not([tabindex="-1"])'
].join(",")

type UseFocusTrapOptions = {
    initialFocus?: "first" | "container"
}

const isVisible = (el: HTMLElement) => {
    if (el.closest("[inert]")) return false
    if (typeof el.checkVisibility === "function") {
        return el.checkVisibility({ checkVisibilityCSS: true })
    }
    return el.getClientRects().length > 0
}

export function useFocusTrap(
    containerRef: RefObject<HTMLElement | null>,
    active: boolean,
    onEscape?: () => void,
    options?: UseFocusTrapOptions
) {
    const onEscapeRef = useRef(onEscape)
    onEscapeRef.current = onEscape
    const optionsRef = useRef(options)
    optionsRef.current = options

    useEffect(() => {
        if (!active) return
        const container = containerRef.current
        if (!container) return

        const previouslyFocused = document.activeElement as HTMLElement | null

        const getFocusable = () =>
            [...container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)].filter(
                isVisible
            )

        const focusInitial = () => {
            if (optionsRef.current?.initialFocus === "container") {
                container.focus()
                return document.activeElement === container
            }
            const first = getFocusable()[0]
            if (!first) return false
            first.focus()
            return document.activeElement === first
        }

        // Opening animations can leave the panel `visibility: hidden` for a
        // few hundred ms (`transition-all` delays the computed value). Retry
        // until a control is actually visible and accepted focus.
        let retryId = 0
        if (!focusInitial()) {
            let attempts = 0
            retryId = window.setInterval(() => {
                attempts += 1
                if (focusInitial() || attempts > 20) window.clearInterval(retryId)
            }, 50)
        }

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                event.preventDefault()
                onEscapeRef.current?.()
                return
            }
            if (event.key !== "Tab") return

            const items = getFocusable()
            if (items.length === 0) {
                event.preventDefault()
                return
            }

            const first = items[0]
            const last = items[items.length - 1]
            const focusInside = container.contains(document.activeElement)

            if (!focusInside) {
                event.preventDefault()
                const fallback = event.shiftKey ? last : first
                fallback.focus()
                return
            }

            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault()
                last.focus()
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault()
                first.focus()
            }
        }

        document.addEventListener("keydown", onKeyDown)
        return () => {
            if (retryId) window.clearInterval(retryId)
            document.removeEventListener("keydown", onKeyDown)
            previouslyFocused?.focus()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active])
}
